import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  InsuranceApiService,
  InsuranceProductListItem,
  InsuranceProductVersionListItem,
  InsuranceProposalDetail,
  InsuranceProductDetail
} from './insurance-api.service';
import { InsuranceAiCopilotPanelComponent } from './insurance-ai-copilot-panel.component';
import {
  PAYMENT_PLAN_OPTIONS,
  parsePartyIds
} from './insurance-proposal-schemas';

type ProposalTab = 'summary' | 'binding' | 'history';

@Component({
  standalone: true,
  selector: 'ain-insurance-proposal-detail',
  imports: [
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    RouterLink,
    FormsModule,
    InputTextModule,
    SelectModule,
    ButtonModule,
    DialogModule,
    TableModule,
    CustomerSelectorComponent,
    PartySelectorComponent,
    JalaliDateInputComponent,
    InfoHintComponent,
    InsuranceAiCopilotPanelComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.proposalDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.proposals.help.detail.title'"
        [descriptionKey]="'insurance.proposals.help.detail.description'"
        [bulletKeys]="[
          'insurance.proposals.help.detail.bullets.lifecycle',
          'insurance.proposals.help.detail.bullets.tabs',
          'insurance.proposals.help.detail.bullets.binding'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (!proposal()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/proposals']">{{ t('catalog.action.backToList') }}</a>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.proposals.sections.info') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.proposals.help.info.title'"
              [descriptionKey]="'insurance.proposals.help.info.description'"
              [bulletKeys]="[
                'insurance.proposals.help.info.bullets.quote',
                'insurance.proposals.help.info.bullets.pricing',
                'insurance.proposals.help.info.bullets.parties'
              ]" />
          </div>
          <div class="summary">
            <span>{{ t('insurance.proposals.fields.proposalNumber') }}: <strong>{{ proposal()!.proposalNumber }}</strong></span>
            <span>{{ t('columns.status') }}: <strong>{{ t(statusKey(proposal()!.status)) }}</strong></span>
            <span>{{ t('insurance.proposals.fields.quoteNumber') }}: <strong>{{ proposal()!.quoteNumber }}</strong></span>
            <span>{{ t('insurance.quotes.fields.pricingHash') }}: <strong>{{ proposal()!.pricingSnapshotHash || '-' }}</strong></span>
          </div>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.proposals.sections.lifecycle') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.proposals.help.lifecycle.title'"
              [descriptionKey]="'insurance.proposals.help.lifecycle.description'"
              [bulletKeys]="[
                'insurance.proposals.help.lifecycle.bullets.submit',
                'insurance.proposals.help.lifecycle.bullets.bind'
              ]" />
          </div>
          <div class="lifecycle-actions">
            <button pButton type="button" [label]="t('insurance.proposalDetail.actions.submit')" (click)="submit()" [disabled]="!canSubmit()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.proposalDetail.actions.cancel')" (click)="cancel()" [disabled]="!canCancel()"></button>
            <button pButton type="button" severity="success" [label]="t('insurance.proposalDetail.actions.bind')" (click)="openBindDialog()" [disabled]="!canBind()"></button>
          </div>
          @if (successMessage()) { <p class="success">{{ successMessage() }}</p> }
          @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'summary'" (click)="activeTab.set('summary')">{{ t('insurance.proposalDetail.tabs.summary') }}</button>
            <button type="button" [class.active]="activeTab() === 'binding'" (click)="activeTab.set('binding')">{{ t('insurance.proposalDetail.tabs.binding') }}</button>
            <button type="button" [class.active]="activeTab() === 'history'" (click)="activeTab.set('history')">{{ t('insurance.proposalDetail.tabs.history') }}</button>
          </div>

          @if (activeTab() === 'summary') {
            <div class="lines-header">
              <h4>{{ t('insurance.proposalDetail.tabs.summary') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.proposals.help.tabs.summary.title'"
                [descriptionKey]="'insurance.proposals.help.tabs.summary.description'"
                [bulletKeys]="[
                  'insurance.proposals.help.tabs.summary.bullets.readonly',
                  'insurance.proposals.help.tabs.summary.bullets.quoteLink'
                ]" />
            </div>
            <div class="grid">
              <label>
                <span>{{ t('insurance.proposals.fields.quoteNumber') }}</span>
                <button type="button" class="link-btn" (click)="openQuote()">{{ proposal()!.quoteNumber }}</button>
              </label>
              <label>
                <span>{{ t('insurance.proposals.fields.boundPolicyId') }}</span>
                @if (proposal()!.boundPolicyId) {
                  <button type="button" class="link-btn" (click)="openPolicy()">{{ t('insurance.proposalDetail.actions.viewPolicy') }}</button>
                } @else {
                  <span>-</span>
                }
              </label>
              <label>
                <span>{{ t('insurance.proposals.fields.status') }}</span>
                <p-select
                  [options]="proposalStatusOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="proposal()!.status"
                  [disabled]="true"
                  appendTo="body"
                  name="summaryStatus" />
              </label>
              <label>
                <span>{{ t('insurance.proposals.fields.policyholderPartyId') }}</span>
                <ain-party-selector [(ngModel)]="policyholderPartyId" name="policyholderPartyId" [disabled]="true" />
              </label>
              <label>
                <span>{{ t('insurance.quotes.fields.customer') }}</span>
                <ain-customer-selector [(ngModel)]="customerAccountId" name="customerAccountId" [disabled]="true" />
              </label>
              <label>
                <span>{{ t('insurance.quotes.fields.productVersion') }}</span>
                <p-select
                  [options]="versionOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="proposal()!.productVersionId"
                  [disabled]="true"
                  appendTo="body"
                  name="summaryProductVersion" />
              </label>
              <label>
                <span>{{ t('insurance.proposalDetail.fields.paymentPlanPreference') }}</span>
                <p-select
                  [options]="paymentPlanOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="proposal()!.paymentPlanPreference || null"
                  [disabled]="true"
                  appendTo="body"
                  name="summaryPaymentPlan" />
              </label>
            </div>

            <div class="section-gap">
              <p class="field-label">{{ t('insurance.proposalDetail.sections.insuredParties') }}</p>
              @if (insuredPartyIds().length === 0) {
                <ain-state type="empty" [title]="t('insurance.proposalDetail.states.noInsuredParties')" />
              } @else {
                <p-table [value]="insuredPartyIds()" styleClass="lines-table">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('insurance.proposalDetail.schemas.party') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-partyId>
                    <tr>
                      <td><ain-party-selector [ngModel]="partyId" [disabled]="true" /></td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            </div>

            <div class="section-gap">
              <p class="field-label">{{ t('insurance.proposalDetail.sections.beneficiaries') }}</p>
              @if (beneficiaryPartyIds().length === 0) {
                <ain-state type="empty" [title]="t('insurance.proposalDetail.states.noBeneficiaries')" />
              } @else {
                <p-table [value]="beneficiaryPartyIds()" styleClass="lines-table">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('insurance.proposalDetail.schemas.party') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-partyId>
                    <tr>
                      <td><ain-party-selector [ngModel]="partyId" [disabled]="true" /></td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            </div>
          } @else if (activeTab() === 'binding') {
            <div class="lines-header">
              <h4>{{ t('insurance.proposalDetail.tabs.binding') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.proposals.help.tabs.binding.title'"
                [descriptionKey]="'insurance.proposals.help.tabs.binding.description'"
                [bulletKeys]="[
                  'insurance.proposals.help.tabs.binding.bullets.dates',
                  'insurance.proposals.help.tabs.binding.bullets.idempotency'
                ]" />
            </div>
            <div class="grid">
              <label>
                <span>{{ t('insurance.proposals.fields.boundPolicyId') }}</span>
                @if (proposal()!.boundPolicyId) {
                  <button type="button" class="link-btn" (click)="openPolicy()">{{ t('insurance.proposalDetail.actions.viewPolicy') }}</button>
                } @else {
                  <span>-</span>
                }
              </label>
              @if (proposal()!.boundAtUtc) {
                <p><strong>{{ t('insurance.proposalDetail.fields.boundAt') }}:</strong> {{ proposal()!.boundAtUtc! | ainDate }}</p>
              }
            </div>
            @if (canBind()) {
              <div class="actions">
                <button pButton type="button" severity="success" [label]="t('insurance.proposalDetail.actions.bind')" (click)="openBindDialog()"></button>
              </div>
            }
          } @else {
            <div class="lines-header">
              <h4>{{ t('insurance.proposalDetail.tabs.history') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.proposals.help.tabs.history.title'"
                [descriptionKey]="'insurance.proposals.help.tabs.history.description'"
                [bulletKeys]="['insurance.proposals.help.tabs.history.bullets.timeline']" />
            </div>
            <div class="summary-grid">
              <label>
                <span>{{ t('insurance.proposals.fields.status') }}</span>
                <p-select
                  [options]="proposalStatusOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="proposal()!.status"
                  [disabled]="true"
                  appendTo="body"
                  name="historyStatus" />
              </label>
              <p><strong>{{ t('insurance.proposalDetail.fields.submittedAt') }}:</strong> {{ proposal()!.submittedAtUtc ? (proposal()!.submittedAtUtc! | ainDate) : '-' }}</p>
              <p><strong>{{ t('insurance.proposalDetail.fields.acceptedAt') }}:</strong> {{ proposal()!.acceptedAtUtc ? (proposal()!.acceptedAtUtc! | ainDate) : '-' }}</p>
              <p><strong>{{ t('insurance.proposalDetail.fields.boundAt') }}:</strong> {{ proposal()!.boundAtUtc ? (proposal()!.boundAtUtc! | ainDate) : '-' }}</p>
              <p><strong>{{ t('insurance.proposalDetail.fields.declinedAt') }}:</strong> {{ proposal()!.declinedAtUtc ? (proposal()!.declinedAtUtc! | ainDate) : '-' }}</p>
              <p><strong>{{ t('insurance.proposalDetail.fields.expiredAt') }}:</strong> {{ proposal()!.expiredAtUtc ? (proposal()!.expiredAtUtc! | ainDate) : '-' }}</p>
            </div>
          }
        </section>

        <ain-insurance-ai-copilot-panel entityType="InsuranceProposal" [entityId]="proposal()!.id" />
      }

      <p-dialog
        [header]="t('insurance.proposalDetail.bindDialog.title')"
        [modal]="true"
        [(visible)]="bindDialogVisible"
        [style]="{ width: '560px' }">
        <div class="dialog-grid">
          <label>
            <span>{{ t('insurance.proposalDetail.fields.effectiveFrom') }}</span>
            <ain-date-input [(ngModel)]="effectiveFromUtc" name="dialogEffectiveFromUtc" />
          </label>
          <label>
            <span>{{ t('insurance.proposalDetail.fields.effectiveTo') }}</span>
            <ain-date-input [(ngModel)]="effectiveToUtc" name="dialogEffectiveToUtc" />
          </label>
          <label class="wide">
            <span>{{ t('insurance.proposalDetail.fields.idempotencyKey') }}</span>
            <input pInputText [(ngModel)]="idempotencyKey" name="dialogIdempotencyKey" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="bindDialogVisible.set(false)"></button>
          <button pButton type="button" severity="success" [label]="t('insurance.proposalDetail.actions.bind')" (click)="bind()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .section-title h3 { margin: 0; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; font-size: 14px; }
    .lifecycle-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 10px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 10px; }
    .lines-header h4 { margin: 0; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid .wide { grid-column: 1 / -1; }
    .section-gap { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e5e7eb; }
    label { display: grid; gap: 6px; }
    .field-label { display: block; color: #475569; font-size: 12px; font-weight: 600; margin-bottom: 6px; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; text-align: start; }
    .success { color: #065f46; margin: 8px 0 0; }
    .error { color: #b91c1c; margin: 8px 0 0; }
    @media (max-width: 760px) {
      .grid, .summary-grid, .dialog-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsuranceProposalDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly proposal = signal<InsuranceProposalDetail | null>(null);
  readonly activeTab = signal<ProposalTab>('summary');
  readonly successMessage = signal('');
  readonly errorMessage = signal('');
  readonly bindDialogVisible = signal(false);
  readonly products = signal<InsuranceProductListItem[]>([]);
  readonly versions = signal<InsuranceProductVersionListItem[]>([]);

  readonly insuredPartyIds = computed(() => parsePartyIds(this.proposal()?.insuredPartiesJson));
  readonly beneficiaryPartyIds = computed(() => parsePartyIds(this.proposal()?.beneficiariesJson));

  idempotencyKey = crypto.randomUUID();
  effectiveFromUtc = new Date().toISOString();
  effectiveToUtc = new Date(Date.now() + (365 * 24 * 60 * 60 * 1000)).toISOString();
  policyholderPartyId = '';
  customerAccountId = '';

  ngOnInit(): void {
    this.api.listProducts().subscribe({
      next: rows => {
        this.products.set(rows);
        this.tryResolveProductContext();
      },
      error: () => this.products.set([])
    });

    const proposalId = this.route.snapshot.paramMap.get('proposalId');
    if (!proposalId) {
      this.loading.set(false);
      return;
    }

    this.api.getProposal(proposalId).subscribe({
      next: item => {
        this.proposal.set(item);
        if (item) {
          this.policyholderPartyId = item.policyholderPartyId;
          this.customerAccountId = item.customerAccountId;
        }
        this.tryResolveProductContext();
        this.loading.set(false);
      },
      error: () => {
        this.proposal.set(null);
        this.loading.set(false);
      }
    });
  }

  pageTitle(): string {
    return this.proposal()?.proposalNumber ?? this.t('insurance.proposalDetail.title');
  }

  proposalStatusOptions(): Array<{ label: string; value: string }> {
    return [
      { value: 'Draft', label: this.t('insurance.proposals.status.draft') },
      { value: 'Submitted', label: this.t('insurance.proposals.status.submitted') },
      { value: 'Accepted', label: this.t('insurance.proposals.status.accepted') },
      { value: 'Declined', label: this.t('insurance.proposals.status.declined') },
      { value: 'Expired', label: this.t('insurance.proposals.status.expired') },
      { value: 'Bound', label: this.t('insurance.proposals.status.bound') }
    ];
  }

  paymentPlanOptions(): Array<{ label: string; value: string | null }> {
    return [
      { value: null, label: this.t('insurance.proposalDetail.paymentPlans.unspecified') },
      ...PAYMENT_PLAN_OPTIONS.map(item => ({
        value: item.value,
        label: this.t(item.labelKey)
      }))
    ];
  }

  versionOptions(): Array<{ label: string; value: string }> {
    const options = this.versions().map(version => ({
      value: version.id,
      label: `v${version.versionNumber} (${version.status})`
    }));
    const current = this.proposal()?.productVersionId;
    if (current && !options.some(item => item.value === current)) {
      options.unshift({ value: current, label: current });
    }
    return options;
  }

  openBindDialog(): void {
    if (!this.canBind()) {
      return;
    }

    this.effectiveFromUtc = new Date().toISOString();
    this.effectiveToUtc = new Date(Date.now() + (365 * 24 * 60 * 60 * 1000)).toISOString();
    this.idempotencyKey = crypto.randomUUID();
    this.bindDialogVisible.set(true);
  }

  openQuote(): void {
    const quoteId = this.proposal()?.quoteId;
    if (quoteId) {
      void this.router.navigate(['/insurance/quotes', quoteId]);
    }
  }

  openPolicy(): void {
    const policyId = this.proposal()?.boundPolicyId;
    if (policyId) {
      void this.router.navigate(['/insurance/policies', policyId]);
    }
  }

  submit(): void {
    const current = this.proposal();
    if (!current) {
      return;
    }
    if (!confirm(this.t('insurance.proposalDetail.messages.submitConfirm'))) {
      return;
    }

    this.api.submitProposal(current.id).subscribe({
      next: item => {
        this.proposal.set(item);
        this.successMessage.set(this.t('insurance.proposalDetail.messages.submitted'));
        this.errorMessage.set('');
      },
      error: () => {
        this.errorMessage.set(this.t('insurance.states.loadError'));
      }
    });
  }

  cancel(): void {
    const current = this.proposal();
    if (!current) {
      return;
    }
    if (!confirm(this.t('insurance.proposalDetail.messages.cancelConfirm'))) {
      return;
    }

    this.api.cancelProposal(current.id).subscribe({
      next: item => {
        this.proposal.set(item);
        this.successMessage.set(this.t('insurance.proposalDetail.messages.cancelled'));
        this.errorMessage.set('');
      },
      error: () => {
        this.errorMessage.set(this.t('insurance.states.loadError'));
      }
    });
  }

  bind(): void {
    const current = this.proposal();
    if (!current) {
      return;
    }
    if (!this.idempotencyKey.trim()) {
      this.errorMessage.set(this.t('insurance.proposalDetail.messages.idempotencyRequired'));
      return;
    }

    this.api.bindProposal(current.id, {
      paymentIntentId: null,
      effectiveFromUtc: this.effectiveFromUtc,
      effectiveToUtc: this.effectiveToUtc
    }, this.idempotencyKey.trim()).subscribe({
      next: item => {
        this.proposal.set(item);
        this.bindDialogVisible.set(false);
        this.successMessage.set(this.t('insurance.proposalDetail.messages.bound'));
        this.errorMessage.set('');
        this.idempotencyKey = crypto.randomUUID();
      },
      error: () => {
        this.errorMessage.set(this.t('insurance.states.loadError'));
      }
    });
  }

  canSubmit(): boolean {
    return this.proposal()?.status === 'Draft';
  }

  canCancel(): boolean {
    const status = this.proposal()?.status;
    return status === 'Draft' || status === 'Submitted' || status === 'Accepted';
  }

  canBind(): boolean {
    const status = this.proposal()?.status;
    return status === 'Accepted' || status === 'Submitted';
  }

  statusKey(status: InsuranceProposalDetail['status']): string {
    return `insurance.proposals.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  private tryResolveProductContext(): void {
    const current = this.proposal();
    if (!current?.productVersionId || this.products().length === 0) {
      return;
    }
    if (this.versions().some(item => item.id === current.productVersionId)) {
      return;
    }

    for (const product of this.products()) {
      this.api.getProduct(product.id).subscribe({
        next: (detail: InsuranceProductDetail | null) => {
          if (!detail?.versions?.some(item => item.id === current.productVersionId)) {
            return;
          }
          this.versions.set(detail.versions ?? []);
        }
      });
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
